import {Icon} from '@wandb/components';
import {
  Content as DropdownContent,
  Portal as DropdownPortal,
  Root as DropdownRoot,
  Separator as DropdownSeparator,
} from '@wandb/weave/components/DropdownMenu';
import React, {useCallback, useState} from 'react';
import {useHistory} from 'react-router-dom';

import {useAgentChatReadonly} from '../../../agentChat/useAgentChatReadonly';
import {useRunDetailsTo} from '../../../components/RunDetails/hooks';
import {
  SlurmRunRowConfirmModal,
  SlurmRunRowMenuItems,
} from '../../../components/Slurm/SlurmRunRowMenuActions';
import {useSlurmJobActions} from '../../../components/Slurm/useSlurmJobActions';
import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import {RenameRunDialog} from '../../../pages/Run/RenameRunDialog';
import {
  type RunMenuAction,
  trackRunMenu,
} from '../../../services/analytics/runMenuEvents';
import type {Ref as CustomRunNamesRef} from '../../../state/views/customRunNames/types';
import type {WBTableRowWithRunSetInfo} from '../../../util/tablecols';
import {openInNewTab} from '../../../util/window';
import {useWBAgentController} from '../../AgentChat';
import {AGENT_NAME} from '../../WBAgent/constants';
import {useTrackWBAgentComposer} from '../../WBAgent/useTrackWBAgent';
import {useRunSelectorFeatureEnabled} from '../RunSelectorFeaturesContext';
import {useRunSelectorProjectInfo} from '../RunSelectorRunSetContext';
import {isCrossProjectRun as isCrossProjectRunFunc} from '../util';
import {RowHeaderMenuOption} from './RowHeaderMenuOption';
import {RowHeaderMenuTrigger} from './RowHeaderMenuTrigger';
import {RunCopyIdentifiersSubmenu} from './RunCopyIdentifiersSubmenu';
import {RunPinningAndBaselineMenuOptions} from './RunPinningAndBaselineMenuOptions';

type RunRowHeaderMenuProps = {
  row: WBTableRowWithRunSetInfo;
  customRunNamesRef: CustomRunNamesRef | undefined;
  onCopyRunName: () => void;
  onCopyRunPath: () => void;
  onDeleteRun: () => void;
  onMoveRun: () => void;
  onStopRun: () => void;
  canStopRun?: boolean;
  visible: boolean;
};

export function RunRowHeaderMenu(props: RunRowHeaderMenuProps) {
  const [menuOpen, setMenuOpenRaw] = useState(false);
  const [hasPrefetched, setHasPrefetched] = useState(false);
  const setMenuOpen = useCallback((open: boolean) => {
    setMenuOpenRaw(open);
    if (open) {
      setHasPrefetched(true);
    }
  }, []);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const isReport = useIsInLoadedReportContext();
  const isEnabled = useRunSelectorFeatureEnabled();
  const history = useHistory();
  const trackRunMenuWithLocation = (action: RunMenuAction) => {
    trackRunMenu({action, location: isReport ? 'report' : 'workspace'});
  };
  const runUrl = useRunDetailsTo(props.row.name, props.row.project);

  const {entityName, projectName} = useRunSelectorProjectInfo();
  const runsetProject = {entityName, name: projectName};
  const isCrossProjectRun = isCrossProjectRunFunc(
    props.row.project,
    runsetProject
  );

  const canEditRunName = isEnabled('edit-run-name');
  const canDeleteRun = isEnabled('delete') && !isCrossProjectRun;
  const canMoveRun = isEnabled('move') && !isCrossProjectRun;
  const canStopRun =
    props.canStopRun && isEnabled('stop-run') && !isCrossProjectRun;

  const slurm = useSlurmJobActions({
    entityName,
    projectName,
    runName: props.row.name,
    skip: !hasPrefetched,
  });
  const [slurmConfirmVariant, setSlurmConfirmVariant] = useState<
    'cancel' | 'requeue' | null
  >(null);
  const showSlurmRowMenuActions =
    slurm.showSlurmActions &&
    !slurm.readOnly &&
    !isCrossProjectRun &&
    props.row.state === 'running';
  const {
    isAvailable: isWBAgentChatAvailable,
    openChat,
    addRunToChat,
  } = useWBAgentController();
  const isChatReadonly = useAgentChatReadonly();
  const {trackMentionAdded} = useTrackWBAgentComposer();

  return (
    <>
      <DropdownRoot open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
        <RowHeaderMenuTrigger
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          visible={menuOpen || props.visible}
          onPrefetch={() => setHasPrefetched(true)}
        />
        <DropdownPortal>
          <DropdownContent
            align="start"
            sideOffset={8}
            alignOffset={-8}
            style={{zIndex: 1}}>
            {isWBAgentChatAvailable && (
              <RowHeaderMenuOption
                data-dd-action-name={`Ask ${AGENT_NAME}: Add run to chat`}
                icon={<Icon icon="lightboard-solid" />}
                label={`Ask ${AGENT_NAME}`}
                disabled={isChatReadonly}
                infoLabel={
                  isChatReadonly ? `${AGENT_NAME} is read-only` : undefined
                }
                onSelect={() => {
                  const result = addRunToChat({
                    name: props.row.name,
                    displayName: props.row.displayName,
                    // A run carries its own project only when cross-project;
                    // otherwise fall back to the route, which is its scope.
                    entityName: props.row.project?.entityName ?? entityName,
                    projectName: props.row.project?.name ?? projectName,
                  });
                  if (!result.ok) {
                    return;
                  }
                  openChat();
                  trackMentionAdded('run_row_menu', 'run');
                }}
              />
            )}
            <RowHeaderMenuOption
              data-dd-action-name="View run logs"
              icon={<Icon icon="code-alt" />}
              label="View run logs"
              onSelect={() => {
                const {pathname, search} = runUrl('logs');
                openInNewTab(`${pathname}${search}`, history);
                trackRunMenuWithLocation('View run details');
              }}
            />
            <RunPinningAndBaselineMenuOptions row={props.row} />
            <DropdownSeparator />
            {canEditRunName && (
              <RowHeaderMenuOption
                data-dd-action-name="Edit run name"
                icon={<Icon icon="pencil-edit" />}
                label="Edit run name"
                onSelect={() => {
                  setIsRenameDialogOpen(true);
                  trackRunMenuWithLocation('Edit run name');
                }}
              />
            )}
            <RunCopyIdentifiersSubmenu
              onCopyRunName={() => {
                props.onCopyRunName();
                trackRunMenuWithLocation('Copy run name');
              }}
              onCopyRunPath={() => {
                props.onCopyRunPath();
                trackRunMenuWithLocation('Copy run path');
              }}
            />
            {(canMoveRun || canDeleteRun || canStopRun) && (
              <DropdownSeparator />
            )}

            {canMoveRun && (
              <RowHeaderMenuOption
                data-dd-action-name="Move run to another group"
                icon={<Icon icon="forward-next" />}
                label="Move to another group"
                onSelect={() => {
                  props.onMoveRun();
                  trackRunMenuWithLocation('Move run groups');
                }}
              />
            )}
            {canDeleteRun && (
              <RowHeaderMenuOption
                data-dd-action-name="Delete run"
                icon={<Icon icon="delete" />}
                label="Delete run"
                onSelect={() => {
                  props.onDeleteRun();
                  trackRunMenuWithLocation('Delete run');
                }}
              />
            )}
            {canStopRun ? (
              <RowHeaderMenuOption
                data-dd-action-name="Stop run"
                icon={<Icon icon="stop" />}
                label="Stop run"
                onSelect={() => {
                  props.onStopRun();
                  trackRunMenuWithLocation('Stop run');
                }}
              />
            ) : null}
            {showSlurmRowMenuActions && (
              <SlurmRunRowMenuItems
                renderSeparator={() => <DropdownSeparator />}
                renderItem={({ddActionName, icon, label, onSelect}) => (
                  <RowHeaderMenuOption
                    data-dd-action-name={ddActionName}
                    icon={<Icon icon={icon} />}
                    label={label}
                    onSelect={onSelect}
                  />
                )}
                onCancel={() => setSlurmConfirmVariant('cancel')}
                onRequeue={() => setSlurmConfirmVariant('requeue')}
              />
            )}
          </DropdownContent>
        </DropdownPortal>
      </DropdownRoot>
      <RenameRunDialog
        isOpen={isRenameDialogOpen}
        runId={props.row.id}
        runName={props.row.name}
        runDisplayName={props.row.displayName}
        customRunNamesRef={props.customRunNamesRef}
        closeDialog={() => setIsRenameDialogOpen(false)}
        isCrossProjectRun={isCrossProjectRun}
      />
      {slurm.showSlurmActions && (
        <SlurmRunRowConfirmModal
          entityName={entityName}
          projectName={projectName}
          runName={props.row.name}
          jobId={slurm.jobId}
          variant={slurmConfirmVariant}
          onClose={() => setSlurmConfirmVariant(null)}
        />
      )}
    </>
  );
}
